import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Filterset } from '@/app/services/filtersetService';
import { SearchableSelect, Option } from './SearchableSelect';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { FiltersetNameExistsError } from '@/shared/hooks/useFilterManagement';
import { apiRoutes } from '@/shared/api/routes';
import { resolveFilterTargetModel } from '@/shared/constants/filterTargetModels';
import { getFiltersetTargetModelLabel } from '@/shared/constants/filtersetTargetModelMappings';
import { useToast } from '@/shared/contexts/ToastContext';
import { generateFilterExpression, parseFilterExpression, sanitizeFilterExpression } from '@/shared/utils/filterUtils';
import { FiltersetEditorModal } from './FiltersetEditorModal';

interface FilterUserOption {
  id: number;
  login: string;
  first_name: string;
  last_name: string;
  middle_initials: string;
}

export interface AppliedFilterEditorProps {
  filterset: Filterset;
  onUpdate: (id: number, data: Partial<Filterset>) => Promise<void>;
  currentExpression?: string | undefined;
  renderTrigger?: (openModal: () => void) => React.ReactNode;
}

export const AppliedFilterEditor: React.FC<AppliedFilterEditorProps> = ({
  filterset,
  onUpdate,
  currentExpression,
  renderTrigger,
}) => {
  const MAX_FILTER_NAME_LENGTH = 200;
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; expression?: string }>({});
  const [editedFilterset, setEditedFilterset] = useState<Partial<Filterset>>(filterset);
  const [exactNameMatch, setExactNameMatch] = useState<Filterset | null>(null);

  const { data: users = [], isLoading: isLoadingUsers } = useQuery<FilterUserOption[]>({
    queryKey: ['applied-filter-editor-users'],
    queryFn: async () => {
      const response = await apiClient.post(apiRoutes.proxy('support/internal/user/select_query'), {
        select: ['id', 'login', 'first_name', 'last_name', 'middle_initials'],
        query: [['equal', 'status', 'ENABLED']],
        options: {
          offset: 0,
          limit: 1000,
          order: [['login', 'asc_nulls_last']],
          calculate_total: false,
          return_results: true,
          select_distinct: true,
        },
      });

      return (response.data.data || []).map((row: (number | string | null)[]) => {
        const id = Number(row[0]);
        const login = String(row[1] || '');
        const first_name = String(row[2] || '');
        const last_name = String(row[3] || '');
        const middle_initials = String(row[4] || '');

        return {
          id,
          login,
          first_name,
          last_name,
          middle_initials,
        };
      });
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const userOptions = useMemo(() => {
    const fetchedOptions = users
      .filter((user: FilterUserOption) => !Number.isNaN(user.id))
      .map((user: FilterUserOption) => {
        const nameParts = [user.first_name, user.middle_initials, user.last_name].filter(Boolean).join(' ').trim();
        const label = nameParts ? `${nameParts} (${user.login})` : user.login;
        return {
          id: String(user.id),
          name: label || String(user.id),
        };
      });

    const hasCurrentUser = fetchedOptions.some((option: Option) => option.id === String(filterset.user?.id || ''));
    if (!hasCurrentUser && filterset.user?.id) {
      const userLabel = filterset.user.login || `${filterset.user.first_name || ''} ${filterset.user.last_name || ''}`.trim();
      fetchedOptions.unshift({
        id: String(filterset.user.id),
        name: userLabel || String(filterset.user.id),
      });
    }

    return fetchedOptions;
  }, [users, filterset.user]);

  const selectedUserOption = useMemo<Option | null>(() => {
    const selectedId = String(editedFilterset.user?.id || filterset.user?.id || '');
    if (!selectedId) {
      return null;
    }

    return userOptions.find((option: Option) => String(option.id) === selectedId) || null;
  }, [editedFilterset.user?.id, filterset.user?.id, userOptions]);

  const resolvedTargetModel = useMemo(
    () => resolveFilterTargetModel(editedFilterset.target_model || ''),
    [editedFilterset.target_model],
  );

  const normalizeEditorExpression = (expression: string): string => {
    const parsedExpression = parseFilterExpression(expression) as Record<string, unknown> & {
      quicksearch?: string;
      search?: string;
    };

    if (parsedExpression.quicksearch !== undefined && parsedExpression.search === undefined) {
      parsedExpression.search = parsedExpression.quicksearch;
    }

    delete parsedExpression.quicksearch;

    return sanitizeFilterExpression(generateFilterExpression(parsedExpression));
  };

  const openModal = () => {
    setEditedFilterset({
      ...filterset,
      expression: normalizeEditorExpression(currentExpression || filterset.expression),
    });
    setExactNameMatch(null);
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) {
      return;
    }

    setIsModalOpen(false);
  };

  const handleUserChange = (selected: Option | Option[] | null) => {
    if (!selected || Array.isArray(selected)) {
      return;
    }

    const userId = Number(selected.id);
    if (Number.isNaN(userId)) {
      return;
    }

    const selectedUser = users.find((user: FilterUserOption) => user.id === userId);

    setEditedFilterset((prev) => ({
      ...prev,
      user: {
        first_name: selectedUser?.first_name || prev.user?.first_name || filterset.user?.first_name || '',
        id: userId,
        last_name: selectedUser?.last_name || prev.user?.last_name || filterset.user?.last_name || '',
        login: selectedUser?.login || prev.user?.login || filterset.user?.login || '',
        middle_initials: selectedUser?.middle_initials || prev.user?.middle_initials || filterset.user?.middle_initials || '',
      },
    }));
  };

  const handleSave = async () => {
    const nextErrors: { name?: string; expression?: string } = {};
    const trimmedName = editedFilterset.name?.trim() || '';

    if (!trimmedName) {
      nextErrors.name = t('shared.appliedFilterEditor.nameRequired', 'Name is required');
    } else if (trimmedName.length >= MAX_FILTER_NAME_LENGTH) {
      nextErrors.name = t('shared.appliedFilterEditor.nameTooLong', 'Name: Is too long (maximum is 200 characters)');
    } else if (exactNameMatch) {
      nextErrors.name = t('shared.appliedFilterEditor.nameAlreadyExists', 'Name: Has already been taken');
    }
    if (!editedFilterset.expression?.trim()) {
      nextErrors.expression = t('shared.appliedFilterEditor.filterExpressionRequired', 'Filter expression is required');
    }

    if (nextErrors.name || nextErrors.expression) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsSaving(true);
      const updatePayload: Partial<Filterset> = {};

      if (editedFilterset.name !== undefined) {
        updatePayload.name = trimmedName;
      }
      if (editedFilterset.target_model !== undefined) {
        updatePayload.target_model = editedFilterset.target_model;
      }
      if (editedFilterset.enabled !== undefined) {
        updatePayload.enabled = editedFilterset.enabled;
      }
      if (editedFilterset.shared !== undefined) {
        updatePayload.shared = editedFilterset.shared;
      }
      if (editedFilterset.expression !== undefined) {
        updatePayload.expression = editedFilterset.expression;
      }
      if (
        editedFilterset.user?.id !== undefined
        && editedFilterset.user.id !== filterset.user?.id
      ) {
        updatePayload.user = { id: editedFilterset.user.id } as Filterset['user'];
      }

      await onUpdate(filterset.id, updatePayload);
      setIsModalOpen(false);
      showToast(t('shared.appliedFilterEditor.updateSuccess', 'Filter set updated successfully'), 'success');
    } catch (err) {
      if (err instanceof FiltersetNameExistsError) {
        showToast(t('shared.appliedFilterEditor.nameAlreadyExists', 'Name: Has already been taken'), 'error');
      } else {
        showToast(t('shared.appliedFilterEditor.updateError', 'Failed to update filter set'), 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openModal)
      ) : (
        <div className="px-6 py-3 border-b border-sidebarHeaderBorder bg-surface flex items-center justify-between min-h-[60px]">
          <span className="text-[16px] leading-[1.05] text-text font-semibold truncate">{filterset.name}</span>
          <button
            type="button"
            className="ml-3 text-primary hover:underline text-sm font-medium whitespace-nowrap"
            onClick={openModal}
            aria-label={t('shared.appliedFilterEditor.editFilterAriaLabel', 'Edit filter {{name}}', { name: filterset.name })}
          >
            {t('shared.appliedFilterEditor.updateButton', 'Update')}
          </button>
        </div>
      )}

      <FiltersetEditorModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={t('shared.appliedFilterEditor.title', 'Edit filter set')}
        name={editedFilterset.name || ''}
        onNameChange={(value) => {
          setEditedFilterset((prev) => ({ ...prev, name: value }));
          setErrors((prev) => {
            const nextErrors = { ...prev };
            delete nextErrors.name;
            return nextErrors;
          });
        }}
        nameError={errors.name}
        targetModel={filterset.target_model}
        excludeFiltersetId={filterset.id}
        originalName={filterset.name}
        onExactNameMatchChange={setExactNameMatch}
        mappingLabel={getFiltersetTargetModelLabel(resolvedTargetModel)}
        showUser
        userOptions={userOptions}
        selectedUserOption={selectedUserOption}
        onUserChange={handleUserChange}
        isLoadingUsers={isLoadingUsers}
        showEnabled
        enabled={!!editedFilterset.enabled}
        onEnabledChange={(checked) => setEditedFilterset((prev) => ({ ...prev, enabled: checked }))}
        showShared
        shared={!!editedFilterset.shared}
        onSharedChange={(checked) => setEditedFilterset((prev) => ({ ...prev, shared: checked }))}
        expression={editedFilterset.expression || ''}
        expressionError={errors.expression}
        editableExpression
        onExpressionChange={(value) => setEditedFilterset((prev) => ({ ...prev, expression: value }))}
        saveLabel={t('shared.appliedFilterEditor.updateButton', 'Update')}
        savingLabel={t('shared.appliedFilterEditor.updatingButton', 'Updating...')}
        onSave={handleSave}
        isSaving={isSaving}
        dataTestId="applied-filter-editor-modal"
      />
    </>
  );
};
